import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, Subject, Subscription, switchMap, timer} from 'rxjs';
import {filter, map, take} from 'rxjs/operators';
import {
	InputCatastoModel,
	JobCatastoModel,
	StatoJobCatasto,
	StatoRaggruppamentoModel
} from '../../../shared/models';
import {PatrimonioImmobiliareService} from './patrimonio-immobiliare.service';
import {DataStateService} from '../state/data-state.service';
import {TranslateService} from '@ngx-translate/core';

/**
 * Segue l'elaborazione di una visura catastale eseguita in background dal backend. Vive a livello di applicazione
 * e non nel wizard, così sopravvive alla chiusura del dialog e ai cambi di pagina.
 */
@Injectable({providedIn: 'root'})
export class CatastoJobService {

	private static readonly INTERVALLO_POLLING_MS = 5000;

	// oltre questo tempo si smette di interrogare il job e lo si considera fallito
	private static readonly DURATA_MASSIMA_POLLING_MS = 15 * 60 * 1000;

	// stato corrente dell'elaborazione, null quando non ce n'è nessuna in corso o conclusa da riprendere
	private readonly jobSubject = new BehaviorSubject<JobCatastoModel | null>(null);
	readonly job$: Observable<JobCatastoModel | null> = this.jobSubject.asObservable();

	// emette una sola volta quando l'elaborazione si conclude: ci si appoggia chi deve avvisare l'utente
	private readonly conclusoSubject = new Subject<JobCatastoModel>();
	readonly concluso$: Observable<JobCatastoModel> = this.conclusoSubject.asObservable();

	// emette quando l'utente chiede di aprire il raggruppamento dall'avviso di fine elaborazione
	private readonly richiestaAperturaSubject = new Subject<void>();
	readonly richiestaApertura$: Observable<void> = this.richiestaAperturaSubject.asObservable();

	// dati con cui riaprire il wizard sullo step del raggruppamento
	private contestoWizard: any = null;

	// input dell'elaborazione in corso: serve a conservare il raggruppamento appena è pronto, anche a wizard chiuso
	private inputCorrente: InputCatastoModel = null;
	private nomeDocumentoCorrente: string = null;

	// dossier a cui appartiene l'elaborazione: il risultato ha senso solo lì dentro
	private rootIdJob: string = null;

	private pollingSub: Subscription;

	constructor(private immobiliareService: PatrimonioImmobiliareService,
	            private state: DataStateService,
	            private translate: TranslateService) {
	}

	/**
	 * Il job filtrato sul dossier attualmente aperto: emette null quando l'utente esce dal dossier a cui
	 * l'elaborazione appartiene, e torna a emetterlo quando ci rientra.
	 *
	 * L'indicatore si basa su questo e non su job$, perché un risultato del dossier A non deve comparire (né
	 * tantomeno essere apribile) mentre si sta lavorando sul dossier B.
	 */
	readonly jobDossierCorrente$: Observable<JobCatastoModel | null> = combineLatest([this.job$, this.state.idRoot$]).pipe(
		map(([job, idRootCorrente]) => (job && this.rootIdJob && this.rootIdJob === idRootCorrente) ? job : null)
	);

	// l'utente ha chiesto di aprire il raggruppamento ma nessuno ha ancora raccolto la richiesta
	private aperturaInSospeso = false;

	richiediApertura(): void {
		this.aperturaInSospeso = true;
		this.richiestaAperturaSubject.next();
	}

	/**
	 * Raccoglie una richiesta di apertura rimasta in sospeso, azzerandola.
	 *
	 * Serve perché l'indicatore è visibile in tutta l'applicazione, mentre il raggruppamento può essere aperto
	 * solo dalla pagina del patrimonio immobiliare: se l'utente clicca da un'altra pagina, la richiesta viene
	 * raccolta al primo ingresso su quella pagina invece di andare persa.
	 */
	consumaRichiestaApertura(): boolean {
		const richiesta = this.aperturaInSospeso;
		this.aperturaInSospeso = false;
		return richiesta;
	}

	get jobCorrente(): JobCatastoModel | null {
		return this.jobSubject.value;
	}

	/**
	 * Messaggio da mostrare all'utente per un'elaborazione fallita.
	 *
	 * Gli errori previsti (visura non intestata al soggetto, codice fiscale non corrispondente, visura
	 * illeggibile) arrivano dal backend con un errorKey e vanno mostrati tradotti; per tutti gli altri resta il
	 * messaggio tecnico, che è l'unica informazione disponibile.
	 */
	messaggioErrore(job: JobCatastoModel | null): string {
		if (!job?.errorKey) {
			return job?.errore ?? '';
		}
		const chiave = `error_${job.errorKey}`;
		const tradotto = this.translate.instant(chiave);
		// instant restituisce la chiave stessa quando la traduzione manca: in quel caso è meglio il messaggio del backend
		return tradotto !== chiave ? tradotto : (job.errore ?? '');
	}

	get inCorso(): boolean {
		return this.jobSubject.value?.stato === StatoJobCatasto.IN_CORSO;
	}

	get risultatoPronto(): boolean {
		return this.jobSubject.value?.stato === StatoJobCatasto.COMPLETATO;
	}

	getContestoWizard(): any {
		return this.contestoWizard;
	}

	/**
	 * Avvia l'elaborazione e inizia a seguirne lo stato.
	 *
	 * @param contestoWizard dati necessari a riaprire il wizard quando il risultato è pronto (soggetto, aziende,
	 *                       flag aziendale): vanno conservati qui perché il dialog nel frattempo può essere chiuso
	 */
	avvia(input: InputCatastoModel, contestoWizard: any, nomeDocumento?: string, forzaRilettura = false): Observable<JobCatastoModel> {
		this.annullaPolling();
		// azzero prima di partire, altrimenti chi si mette in attesa del risultato riceverebbe subito quello
		// dell'elaborazione precedente
		this.jobSubject.next(null);
		this.contestoWizard = contestoWizard;
		this.rootIdJob = input.rootId;
		this.inputCorrente = input;
		this.nomeDocumentoCorrente = nomeDocumento;
		const avvio$ = this.immobiliareService.avviaJobCatasto(input, forzaRilettura);
		avvio$.subscribe(job => {
			this.jobSubject.next(job);
			this.avviaPolling(job.jobId);
		});
		return avvio$;
	}

	/**
	 * Da chiamare quando il risultato è stato consumato (immobili salvati o elaborazione scartata), altrimenti
	 * l'avviso continuerebbe a riproporsi ad ogni ritorno sulla pagina.
	 */
	pulisci(): void {
		this.annullaPolling();
		this.contestoWizard = null;
		this.rootIdJob = null;
		this.aperturaInSospeso = false;
		this.jobSubject.next(null);
	}

	private avviaPolling(jobId: string, inizioMs = Date.now()): void {
		this.pollingSub = timer(CatastoJobService.INTERVALLO_POLLING_MS, CatastoJobService.INTERVALLO_POLLING_MS)
			.pipe(switchMap(() => this.immobiliareService.getStatoJobCatasto(jobId)))
			.subscribe({
				next: job => {
					this.jobSubject.next(job);
					if (job.stato !== StatoJobCatasto.IN_CORSO) {
						this.annullaPolling();
						this.conservaProposta(job);
						this.conclusoSubject.next(job);
						return;
					}
					// se il backend viene riavviato mentre elabora, il job resta IN_CORSO per sempre: senza
					// questo limite il client continuerebbe a interrogarlo all'infinito
					if (Date.now() - inizioMs > CatastoJobService.DURATA_MASSIMA_POLLING_MS) {
						this.annullaPolling();
						const scaduto = {...job, stato: StatoJobCatasto.ERRORE, errore: this.translate.instant('catastoWizard.elaborazioneScaduta')};
						this.jobSubject.next(scaduto);
						this.conclusoSubject.next(scaduto);
					}
				},
				// un errore di rete non deve interrompere il monitoraggio: l'elaborazione prosegue lato server,
				// quindi si riprova al giro successivo
				error: () => this.avviaPolling(jobId)
			});
	}

	private annullaPolling(): void {
		this.pollingSub?.unsubscribe();
		this.pollingSub = null;
	}

	/**
	 * Conserva il raggruppamento appena letto dalla visura, così resta a disposizione anche se l'utente chiude il
	 * wizard senza salvare. Sta qui e non nel wizard proprio perché il wizard può non essere più aperto.
	 * Il backend scarta la proposta se per quel documento c'è già una versione sistemata dall'utente.
	 */
	private conservaProposta(job: JobCatastoModel): void {
		if (job.stato !== StatoJobCatasto.COMPLETATO || !this.inputCorrente || !job.risultato?.beniImmobili?.length) {
			return;
		}
		const gruppi = job.risultato.beniImmobili;
		const stato: StatoRaggruppamentoModel = {
			beniSingoli: gruppi.filter(g => g.length === 1).flat(),
			beniRaggruppati: gruppi.filter(g => g.length > 1)
		};
		this.immobiliareService.salvaPropostaRaggruppamento({
			rootId: this.inputCorrente.rootId,
			soggettoId: this.inputCorrente.soggettoId,
			documentoId: this.inputCorrente.documentoId,
			nomeDocumento: this.nomeDocumentoCorrente,
			formatoVisura: job.risultato.formatoVisura,
			nrImmobili: gruppi.flat().length,
			raggruppamento: JSON.stringify(stato)
			// se non riesce non è un problema dell'utente: il risultato resta comunque a video
		}).subscribe({error: () => console.warn('Raggruppamento non conservato')});
	}

	/**
	 * Emette il risultato non appena disponibile: se l'elaborazione è già conclusa risponde subito, altrimenti
	 * attende la conclusione. Usato dal wizard quando viene riaperto mentre il lavoro è ancora in corso.
	 */
	attendiRisultato(): Observable<JobCatastoModel> {
		return this.job$.pipe(
			filter(job => !!job && job.stato !== StatoJobCatasto.IN_CORSO),
			take(1)
		) as Observable<JobCatastoModel>;
	}
}
